import type {
  ImageStyle,
  MotionDesignOptions,
  ReelMode,
  VideoMode,
} from '../../types';
import {TITLE_MAX} from '../../limits';
import {createText} from '../../createText';

/** A choice as the Studio server lists it in /options. */
export interface Choice {
  id: string;
  label: string;
  hint?: string;
}

export interface StudioOptions {
  /** Missing on older servers, which enforce AI credits. */
  enforceAiCredits?: boolean;
  lengths: {id: string; label: string; targetSeconds: number}[];
  endings: Choice[];
  visuals: Choice[];
  imageStyles: Choice[];
  capabilities: Record<string, boolean>;
  /** The largest document the server takes, in MB; older servers leave it out. */
  maxUploadMb?: number | null;
  /** Deliveries an English library voice can be asked for; empty or missing without the library. */
  voiceEmotions?: Choice[];
  /** Emma and James as the server describes them. */
  voices?: {name: string; description?: string}[];
  /**
   * The motion choices the server offers (see readMotionDesign); null or missing on older
   * servers, which get no motion field.
   */
  motionDesign?: MotionDesignOptions | null;
}

export interface LibraryFile {
  id: string;
  fileName: string;
  fileType?: string;
  /** The library's signed storage URL, which the source preview reads. */
  fileUrl?: string;
  /** The library's thumbnail of the file. */
  placeholderUrl?: string;
  /** In MB, as the library lists it. */
  fileSize?: number;
  /** A video's length in seconds, when the library recorded one. */
  duration?: number;
}

/** Opens the library on documents, and on videos too when `videos` is set; resolves with the pick. */
export type BrowseDocument = (options?: {
  videos?: boolean;
}) => Promise<LibraryFile | null | undefined>;
/** Opens the library on audio files; resolves with the pick. */
export type BrowseAudio = () => Promise<LibraryFile | null | undefined>;

export type SourceType = 'prompt' | 'document';
export type CategoryStatus = 'loading' | 'ready' | 'error';

export interface CreateForm {
  title: string;
  is_public: boolean;
  prompt: string;
  lengthBand: string;
  backgroundMusic: string;
  ending: string;
  language: string;
  region: string;
  /** Empty while a language only library voices speak waits for one. */
  voiceName: string;
  visuals: string;
  /** The brief's media source: 'ai' for still images, 'storyblocks' for stock footage (see VIDEO_TYPES). */
  mediaSource: string;
}

// Music matches the New microreel page: no music, one of Tapybl's tracks, or an
// audio file from the library, sent as 'library:<file id>'.
export const NO_MUSIC = 'None';
export const LIBRARY_MUSIC = 'library';

export const DEFAULT_FORM: CreateForm = {
  title: '',
  is_public: false,
  prompt: '',
  lengthBand: '30-60s',
  backgroundMusic: NO_MUSIC,
  ending: 'knowledge_check',
  language: 'en',
  region: 'US',
  voiceName: 'James',
  visuals: 'generated',
  mediaSource: 'ai',
};

// Fallback choices if /options can't be fetched (mirrors Tapybl's Create Microreel wizard).
export const DEFAULT_OPTIONS: StudioOptions = {
  lengths: [
    {id: '30-60s', label: createText.lengthShort, targetSeconds: 50},
    {id: '1-2min', label: createText.lengthMedium, targetSeconds: 90},
    {id: '2-5min', label: createText.lengthLong, targetSeconds: 180},
  ],
  endings: [
    {id: 'knowledge_check', label: createText.endingKnowledgeCheck},
    {id: 'policy_attestation', label: createText.endingPolicyAttestation},
    {id: 'none', label: createText.endingNone},
  ],
  visuals: [
    {
      id: 'source',
      label: createText.visualsSource,
      hint: createText.visualsSourceHint,
    },
    {
      id: 'partial',
      label: createText.visualsPartial,
      hint: createText.visualsPartialHint,
    },
    {
      id: 'generated',
      label: createText.visualsGenerated,
      hint: createText.visualsGeneratedHint,
    },
  ],
  // Older APIs ignore imageStyle, so only advertise styles the server supports.
  imageStyles: [],
  capabilities: {planner: true, images: true, voice: true},
};

// Emma and James, by the name the brief sends. Narration style no longer changes the
// voice, so the form leaves it to the server.
export const VOICES = [
  {id: 'Emma', gender: 'female'},
  {id: 'James', gender: 'male'},
] as const;
/** Voices chosen from Cartesia's library, by the name the brief sends. */
export const isLibraryVoice = (voiceName: string) =>
  voiceName.startsWith('cartesia:');

/** The choice that leaves a library voice's delivery to the script. */
export const NATURAL_DELIVERY: Choice = {
  id: '',
  label: createText.naturalDelivery,
  hint: createText.naturalDeliveryHint,
};

// Only the files Studio has been tested with: PDF, DOCX and PPTX documents and
// MP4 videos. Office and PDF files by type as well as extension, so pickers
// that filter by type still offer Word and PowerPoint files.
export const ACCEPT = [
  '.pdf',
  'application/pdf',
  '.docx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pptx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
].join(',');
export const MP4_ACCEPT = '.mp4,video/mp4';
const DOCUMENT_TYPES = /\.(pdf|docx|pptx)$/i;
export const isDocumentName = (name: string) => DOCUMENT_TYPES.test(name);
const MP4_FILE = /\.mp4$/i;
export const isMp4Upload = (file: File) => MP4_FILE.test(file.name);
// The library's upload dialog offers .m4v, an MP4 container it stores as MP4. A type label alone
// never admits another container, so files are still told apart by name.
const LIBRARY_MP4_FILE = /\.(mp4|m4v)$/i;
export const isMp4LibraryFile = (file: LibraryFile) =>
  LIBRARY_MP4_FILE.test(file.fileName);

/** A file's size for the source card: small files in KB, so they never read "0.0 MB". */
export const fileSizeText = (bytes: number) =>
  bytes < 1024 * 1024
    ? createText.kilobytes(Math.max(1, Math.round(bytes / 1024)))
    : createText.megabytes((bytes / 1024 / 1024).toFixed(1));
/** The document limit of a server that doesn't send `maxUploadMb`. */
export const DEFAULT_MAX_UPLOAD_MB = 20;

/** The title a chosen file suggests: its name without the extension, as long as a title can be. */
export const titleFromFileName = (name: string) =>
  name.replace(/\.[^.]+$/, '').slice(0, TITLE_MAX);

// As on the New microreel page, the source preview draws a PDF itself and shows
// Word and PowerPoint files in Microsoft's Office viewer. The viewer fetches the
// file, so it is given the library's (expiring) signed URL.
const PDF_FILE = /\.pdf$/i;
const OFFICE_FILE = /\.(docx|pptx)$/i;
export const isPdfName = (name: string) => PDF_FILE.test(name);
export const isOfficeName = (name: string) => OFFICE_FILE.test(name);
export const officeViewerUrl = (fileUrl: string) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    fileUrl,
  )}`;

/** What the author chose as the source: a document or a video. */
export type SourceKind = 'document' | 'video';

export const reelModes = (
  kind: SourceKind,
): {id: ReelMode; label: string; hint: string}[] => [
  {
    id: 'single',
    label: createText.reelSingle,
    hint:
      kind === 'video'
        ? createText.reelSingleVideoHint
        : createText.reelSingleDocumentHint,
  },
  {
    id: 'per_topic',
    label: createText.reelPerTopic,
    hint:
      kind === 'video'
        ? createText.reelPerTopicVideoHint
        : createText.reelPerTopicDocumentHint,
  },
];

export const VIDEO_MODES: {id: VideoMode; label: string; hint: string}[] = [
  {
    id: 'clipping',
    label: createText.videoModeClipping,
    hint: createText.videoModeClippingHint,
  },
  {
    id: 'animated',
    label: createText.videoModeAnimated,
    hint: createText.videoModeAnimatedHint,
  },
];

/** What the scenes show: still pictures (generated, or the source's own), or stock footage. */
export type VideoType = 'still' | 'stock';

export const VIDEO_TYPES: {id: VideoType; label: string; hint: string}[] = [
  {
    id: 'still',
    label: createText.videoTypeStill,
    hint: createText.videoTypeStillHint,
  },
  {
    id: 'stock',
    label: createText.videoTypeStock,
    hint: createText.videoTypeStockHint,
  },
];

/**
 * The brief's media source for a video type. Still images are AI images beside the source's
 * pictures; stock footage is Storyblocks' clips in every scene. The server also takes 'both',
 * which this form no longer offers.
 */
export const mediaSourceFor = (videoType: VideoType) =>
  videoType === 'stock' ? 'storyblocks' : 'ai';

// With stock footage, the server's visuals choices describe footage instead of AI images.
export const STOCK_VISUALS: Record<string, Pick<Choice, 'label' | 'hint'>> = {
  partial: {
    label: createText.visualsPartialStock,
    hint: createText.visualsPartialStockHint,
  },
  generated: {
    label: createText.visualsGeneratedStock,
    hint: createText.visualsGeneratedStockHint,
  },
};

export const sourceChoices = (videos: boolean): Choice[] => [
  {
    id: 'prompt',
    label: createText.sourcePrompt,
    hint: createText.sourcePromptHint,
  },
  {
    id: 'document',
    label: createText.sourceFile,
    hint: videos ? createText.sourceFileVideoHint : createText.sourceFileHint,
  },
];

export const DEFAULT_IMAGE_STYLE: ImageStyle = 'illustration';

/** The pages to leave out of a document, from "1, 3-5"; throws a message for the author. */
export function parseExcludedPages(value: string) {
  if (!value.trim()) return [];
  const pages = new Set<number>();
  for (const part of value.split(',')) {
    const match = part.trim().match(/^(\d+)(?:\s*-\s*(\d+))?$/);
    if (!match) throw new Error(createText.pagesFormat);
    const first = Number(match[1]);
    const last = match[2] ? Number(match[2]) : first;
    if (first < 1 || last < first || last > 10000 || last - first > 1000)
      throw new Error(createText.pagesRange);
    for (let page = first; page <= last; page++) pages.add(page);
  }
  return Array.from(pages).sort((a, b) => a - b);
}
